import api from './api';

export const fetchCustomDomains = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/custom-domains`);
  return response.data;
};

export const createCustomDomain = async (orgId, { domain, targetType, frontendProjectId } = {}) => {
  const body = { domain };
  if (targetType && targetType !== 'app') body.target_type = targetType;
  if (frontendProjectId) body.frontend_project_id = frontendProjectId;
  const response = await api.post(`/orgs/${orgId}/custom-domains`, body);
  return response.data;
};

export const deleteCustomDomain = async (orgId, domainId) => {
  const response = await api.delete(`/orgs/${orgId}/custom-domains/${domainId}`);
  return response.data;
};

// Org-scoped: returns only the projects belonging to orgId. The backend filters
// server-side (and rejects an org outside the caller's scope), so this list is
// never a superset of what the caller may attach or release.
export const fetchFrontendProjects = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/frontend-projects`);
  return response.data;
};

// Bespoke frontends mounted at a path prefix on an app-type domain, so one
// hostname serves the org's platform apps at /{appId} and custom frontends at
// /{mountPrefix}/. Not applicable to target_type=frontend domains — those
// already serve one build at their root.
export const fetchDomainFrontends = async (orgId, domainId) => {
  const response = await api.get(`/orgs/${orgId}/custom-domains/${domainId}/frontends`);
  return response.data;
};

export const addDomainFrontend = async (orgId, domainId, { frontendProjectId, mountPrefix } = {}) => {
  const response = await api.post(`/orgs/${orgId}/custom-domains/${domainId}/frontends`, {
    frontend_project_id: frontendProjectId,
    mount_prefix: mountPrefix,
  });
  return response.data;
};

export const deleteDomainFrontend = async (orgId, domainId, mountId) => {
  const response = await api.delete(`/orgs/${orgId}/custom-domains/${domainId}/frontends/${mountId}`);
  return response.data;
};

// Branding, app-type domains only. fetchDomainBranding hits the same public,
// unauthenticated endpoint the served app itself reads — there's no admin-only
// "current branding" read, so this doubles as the admin-panel preview fetch.
export const fetchDomainBranding = async (domain) => {
  const response = await api.get('/public/custom-domains/branding', { params: { domain } });
  return response.data;
};

// ONE write for every branding field. Each part is optional and independent, so
// the caller sends only what changed:
//
//   title       browser tab title
//   faviconFile .ico .png .jpg .jpeg .svg
//   logoFile    .png .jpg .jpeg, at least 512x512 — the backend renders the
//               install icons (192, 512, maskable) from it
//   loginHtml   the login page markup itself, pasted (not a file) — a layout
//               carrying #login-widget. An empty string removes it and switches
//               the feature off.
//   enabled     whether the login page is served (independent of uploading one)
//   clear       array of asset names to remove: 'favicon', 'logo', 'login-html'
//
// The server validates and renders EVERYTHING before writing anything, so a
// rejected field fails the whole call with its own message and leaves no
// partial state — which is why sending several at once is safe.
export const updateDomainBranding = async (
  orgId,
  domainId,
  { title, faviconFile, logoFile, loginHtml, enabled, clear } = {},
) => {
  const formData = new FormData();
  if (title !== undefined) formData.append('title', title);
  if (faviconFile) formData.append('favicon', faviconFile);
  if (logoFile) formData.append('logo', logoFile);
  // Sent whenever defined, empty string included — that is how a page is
  // cleared, so this must not be truthiness-gated like the file fields.
  if (loginHtml !== undefined) formData.append('login_html', loginHtml);
  if (enabled !== undefined) formData.append('enabled', enabled ? 'true' : 'false');
  if (clear?.length) formData.append('clear', clear.join(','));
  // The shared `api` instance defaults to Content-Type: application/json,
  // which overrides the multipart boundary the browser would otherwise set
  // for FormData — clearing it here lets the browser fill in the correct
  // "multipart/form-data; boundary=..." header itself.
  const response = await api.put(`/orgs/${orgId}/custom-domains/${domainId}/branding`, formData, {
    headers: { 'Content-Type': undefined },
  });
  return response.data;
};
